/**
 * Passive capture for the Diagnostics panel: listeners that feed the ring buffer (eventLog.ts)
 * from the first line of main.ts on. Nothing here touches the DOM or starts a timer apart from
 * the log's own throttled write to localStorage.
 */
import { EventLog } from "./eventLog";
import { pathOnly, redactText } from "./redact";

let active: EventLog | null = null;

/** The running page's log, or null before installCapture(). */
export function diagLog(): EventLog | null {
  return active;
}

/** A Player lifecycle mark (walk open, ready, close, update...). No-op before installCapture(). */
export function diagMark(kind: string, message = ""): void {
  active?.add(kind, message);
}

function describeArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg) ?? String(arg);
  } catch {
    return String(arg);
  }
}

function displayMode(): string {
  try {
    for (const mode of ["fullscreen", "standalone", "minimal-ui", "browser"]) {
      if (matchMedia(`(display-mode: ${mode})`).matches) return mode;
    }
  } catch {
    // matchMedia missing
  }
  return "unknown";
}

function sessionId(): string {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    return Math.random().toString(36).slice(2, 10);
  }
}

/** Console (still forwarded), errors, rejections and CSP violations of one window into the log. */
function hookWindow(win: Window, add: (kind: string, message: string) => void, label = ""): void {
  const w = win as Window & typeof globalThis;
  let inConsole = false;
  for (const level of ["error", "warn"] as const) {
    const original = w.console[level].bind(w.console);
    w.console[level] = (...args: unknown[]): void => {
      original(...args);
      if (inConsole) return;
      inConsole = true;
      try {
        add(`${label}console.${level}`, args.map(describeArg).join(" "));
      } catch {
        // Logging must never break the page.
      } finally {
        inConsole = false;
      }
    };
  }
  w.addEventListener(
    "error",
    (event: Event) => {
      if (event instanceof ErrorEvent) {
        add(`${label}error`, `${event.message} at ${pathOnly(event.filename || "")}:${event.lineno}:${event.colno}`);
      } else {
        // Resource that failed to load (script, image, iframe...): capture phase only.
        const target = event.target as (Element & { src?: string; href?: string }) | null;
        add(`${label}resource-error`, `${target?.tagName?.toLowerCase() ?? "?"} ${pathOnly(target?.src || target?.href || "")}`);
      }
    },
    true,
  );
  w.addEventListener("unhandledrejection", (event) => add(`${label}rejection`, describeArg(event.reason)));
  w.addEventListener("securitypolicyviolation", (event) => {
    add(`${label}csp`, `${event.violatedDirective} blocked ${redactText(event.blockedURI)}`);
  });
}

/** The walk's viewer iframe (same origin): called on its load event. Unreachable windows are skipped. */
export function diagHookViewer(win: Window | null): void {
  const log = active;
  if (!log || !win) return;
  try {
    hookWindow(win, (kind, message) => log.add(kind, message), "viewer ");
  } catch {
    // Cross-origin or an error page: nothing to hook.
  }
}

/** Installs the listeners once; later calls return the same log. */
export function installCapture(): EventLog {
  if (active) return active;
  let storage: Storage | null = null;
  try {
    storage = localStorage;
  } catch {
    // Blocked: memory-only log.
  }
  const log = new EventLog({ sessionId: sessionId(), storage });
  active = log;
  const add = (kind: string, message: string): void => log.add(kind, message);

  hookWindow(window, add);
  addEventListener("online", () => add("online", ""));
  addEventListener("offline", () => add("offline", ""));
  addEventListener("pageshow", (event) => add("pageshow", event.persisted ? "from bfcache" : ""));
  addEventListener("pagehide", (event) => {
    add("pagehide", event.persisted ? "going to bfcache" : "");
    log.flush();
  });
  document.addEventListener("visibilitychange", () => {
    add("visibility", document.visibilityState);
    if (document.visibilityState === "hidden") log.flush(); // a hidden page may be killed with no further event
  });
  document.addEventListener("freeze", () => {
    add("freeze", "");
    log.flush();
  });
  document.addEventListener("resume", () => add("resume", ""));

  const sw = navigator.serviceWorker;
  if (sw) {
    sw.addEventListener("controllerchange", () => add("sw", `controllerchange -> ${sw.controller?.state ?? "none"}`));
    const watch = (worker: ServiceWorker | null, label: string): void => {
      if (!worker) return;
      worker.addEventListener("statechange", () => add("sw", `${label} worker ${worker.state}`));
    };
    void sw.getRegistration().then((registration) => {
      if (!registration) return;
      watch(registration.installing, "installing");
      watch(registration.waiting, "waiting");
      watch(registration.active, "active");
      registration.addEventListener("updatefound", () => {
        add("sw", "updatefound");
        watch(registration.installing, "new");
      });
    }).catch(() => undefined);
  }

  let navigationType = "?";
  try {
    navigationType = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type ?? "?";
  } catch {
    // no Navigation Timing
  }
  add(
    "start",
    `v${__APP_VERSION__} ${displayMode()} nav=${navigationType} visibility=${document.visibilityState} controller=${sw?.controller?.state ?? "none"} online=${navigator.onLine}`,
  );
  return log;
}
